import { supabase } from '../supabase';
import {
  DataMigration,
  CreateMigrationPayload,
  MigrationSnapshot,
} from '../utils/types';

/**
 * Get all migrations for current exporter (optionally filter by species)
 */
export const getMigrationHistory = async (
  exporterId: number,
  speciesId?: number
): Promise<DataMigration[]> => {
  let query = supabase
    .from('data_migration_history')
    .select(
      `
      *,
      created_by_user:users!created_by(id, first_name, last_name),
      rolled_back_by_user:users!rolled_back_by(id, first_name, last_name),
      species:species(id, name)
    `
    )
    .eq('exporter_id', exporterId)
    .order('executed_at', { ascending: false });

  // Only filter by species if provided
  if (speciesId) {
    query = query.eq('species_id', speciesId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

/**
 * Preview how many records will be affected (before executing)
 */
export const previewMigration = async (
  exporterId: number,
  payload: CreateMigrationPayload
): Promise<number> => {
  const { data, error } = await supabase.rpc('preview_data_migration', {
    p_exporter_id: exporterId,
    p_species_id: payload.speciesId,
    p_target_table: payload.targetTable,
    p_target_column: payload.targetColumn,
    p_old_value: payload.oldValue,
    p_filter_season: payload.filterSeason || null,
  });

  if (error) throw error;
  return Number(data || 0);
};

/**
 * Execute migration with automatic snapshot creation
 */
export const executeMigration = async (
  exporterId: number,
  payload: CreateMigrationPayload
): Promise<{ success: true; migrationId: number; affectedCount: number }> => {
  const { data, error } = await supabase.rpc('execute_data_migration', {
    p_exporter_id: exporterId,
    p_species_id: payload.speciesId,
    p_target_table: payload.targetTable,
    p_target_column: payload.targetColumn,
    p_old_value: payload.oldValue,
    p_new_value: payload.newValue,
    p_filter_season: payload.filterSeason || null,
    p_filter_date_from: payload.filterDateFrom || null,
    p_filter_date_to: payload.filterDateTo || null,
    p_notes: payload.notes || null,
  });

  if (error) throw error;

  return {
    success: true,
    migrationId: Number(data?.migrationId || 0),
    affectedCount: Number(data?.affectedCount || 0),
  };
};

/**
 * Check if a migration can be rolled back (must be latest completed migration)
 */
export const canRollback = async (
  migrationId: number
): Promise<{
  canRollback: boolean;
  reason?: string;
}> => {
  // Get the migration
  const { data: migration, error } = await supabase
    .from('data_migration_history')
    .select('*')
    .eq('id', migrationId)
    .single();

  if (error || !migration) {
    return { canRollback: false, reason: 'Migración no encontrada' };
  }

  if (migration.status !== 'completed') {
    return { canRollback: false, reason: 'Ya fue revertida' };
  }

  // Check if there are any newer completed migrations for same exporter
  const { count } = await supabase
    .from('data_migration_history')
    .select('*', { count: 'exact', head: true })
    .eq('exporter_id', migration.exporter_id)
    .eq('status', 'completed')
    .gt('executed_at', migration.executed_at);

  if (count && count > 0) {
    return {
      canRollback: false,
      reason: `Debes revertir las ${count} migraciones más recientes primero`,
    };
  }

  return { canRollback: true };
};

/**
 * Rollback a migration using snapshots (sequential rollback enforced)
 */
export const rollbackMigration = async (
  migrationId: number
): Promise<{ success: boolean; revertedCount?: number; error?: string }> => {
  try {
    // Check if can rollback
    const { canRollback: canRollbackResult, reason } =
      await canRollback(migrationId);

    if (!canRollbackResult) {
      return { success: false, error: reason };
    }

    const { data, error } = await supabase.rpc('rollback_data_migration', {
      p_migration_id: migrationId,
    });

    if (error) throw error;

    return {
      success: Boolean(data?.success),
      revertedCount:
        data?.revertedCount === undefined
          ? undefined
          : Number(data.revertedCount),
      error: data?.error,
    };
  } catch (error) {
    console.error('Rollback failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get snapshots for a migration (for preview before rollback)
 */
export const getMigrationSnapshots = async (
  migrationId: number
): Promise<MigrationSnapshot[]> => {
  const { data, error } = await supabase
    .from('data_migration_snapshots')
    .select('*')
    .eq('migration_id', migrationId)
    .limit(100); // Limit for preview

  if (error) throw error;
  return data || [];
};
